// Types
export * from './types/global';
export * from './types/normalised-options/normalise';
export * from './types/normalised-options/normalisedAreaSeries';
export * from './types/normalised-options/normalisedAxisOptions';
export * from './types/normalised-options/normalisedCartesianSeries';
export * from './types/normalised-options/normalisedCommonOptions';
export * from './types/normalised-options/normalisedChartCaptionOptions';
export * from './types/normalised-options/normalisedDonutSeries';
export * from './types/normalised-options/normalisedGradientLegendOptions';
export * from './types/normalised-options/normalisedPieSeries';
export * from './types/normalised-options/normalisedSeriesMarkerOptions';
export * from './types/normalised-options/normalisedLabelOptions';
export * from './types/normalised-options/normalisedLegendOptions';
export * from './types/normalised-options/normalisedSeriesArea';
export * from './types/normalised-options/normalisedSelectionOptions';
export * from './types/normalised-options/normalisedZoomOptions';
export * from './types/normalised-options/resolved';
export * from './modules/moduleDefinition';
export * from './types/scene';
export * from './types/scales';

// Structures
export * from './structures/bitfield';
export * from './structures/eventEmitter';
export * from './structures/lruCache';
export * as Debug from './logging/debugLogger';
export { Logger, ambientLogger, isLogLevel } from './logging/logger';
export type { LogIssue, LogLevel } from './logging/logger';
// Chart-less logging fallback, restricted by the `no-unscoped-logger` lint rule. Enumerated rather
// than `export *` so the namespace cannot reach the `Logger` constructor.
export * as ambientLog from './logging/ambientLog';
export * as DebugMetrics from './logging/debugMetrics';
export * from './modules/enterpriseRegistry';
export * as ModuleRegistry from './modules/moduleRegistry';
export { ModuleScope, type RegistryRevision, type ScopedCache, createScopedCache } from './modules/moduleScope';
export { AbstractModuleInstance } from './modules/moduleInstance';
export { type DynamicContext, type DynamicContextApi, createDynamicContext } from './module/dynamicContext';

// Scales
export * from './scale/colorScaleUtil';

// Config
export * from './config/chartDefaults';
export * from './config/optionsDefaults';
export * from './config/gaugePreset';
export * from './config/themeUtil';

// API
export * from './state/memento';
export { ReactiveState } from './state/reactiveState';

// Chart
export * from './types/animationPhase';
export * from './types/axisDirection';
export * from './types/updateType';
export * from './types/zIndexMap';
export * from './chart/legendUtil';
export * from './chart/interpolationProperties';
export * from './utils/aggregation';
export * from './types/geojson';
export * from './types/themeConstants';
export * from './types/themeSymbols';
export * from './types/text';

// Core utilities
export * from './state/callbackCache';
export * from './utils/types/decorator';
export * from './utils/dom/agDocument';
export * from './utils/dom/domUtil';
export * from './utils/geometry/math';
export * from './utils/data/arrays';
export * from './utils/geometry/angle';
export * from './utils/async';
export * from './utils/dom/attributeUtil';
export * from './utils/geometry/border';
export * from './utils/geometry/boxBounds';
export * from './utils/data/binarySearch';
export * from './utils/format/color';
export * from './utils/canvas';
export * from './utils/configuredCanvasMixin';
export * from './state/caching';
export * from './utils/time/date';
export * from './state/cleanupRegistry';
export * from './utils/data/diff';
export * from './utils/data/epochColumns';
export * from './utils/geometry/distance';
export * from './utils/data/extent';
export * from './utils/format/format.util';
export * from './utils/functions';
export * from './utils/geojson';
export * from './structures/graph';
export * from './utils/data/json';
export * from './utils/dom/keynavUtil';
export * from './identity/id';
export * from './types/idBranding';
export * from './utils/data/iso8601';
export * from './utils/data/iterators';
export * from './utils/data/linkedList';
export * from './state/memo';
export * from './utils/data/nearest';
export * from './utils/data/numberArray';
export * from './utils/data/numbers';
export * from './utils/data/object';
export * from './utils/geometry/padding';
export * from './utils/geometry/placement';
export * from './state/properties';
export * from './state/proxy';
export * from './utils/data/strings';
export * from './state/stateMachine';
export * from './rendering/textMeasurer';
export * from './utils/dom/domElements';
export * from './utils/dom/domEvents';
export * from './utils/dom/globalsProxy';
export * from './utils/dom/domDownload';
export * from './utils/text/labelMeasure';
export * from './utils/text/textUtils';
export * from './utils/text/textWrapper';
export * from './utils/time/ticks';
export * from './utils/time/time';
export * from './utils/time/timeFormatDefaults';
export * from './utils/data/visibleRange';
export * from './identity/idGenerator';
export * from './utils/types/typeGuards';
export * from './utils/data/value';
export * from './state/validation';
export * as Vec2 from './utils/geometry/vector';
export * as Vec4 from './utils/geometry/vector4';
export * from './utils/geometry/fill';
export * from './utils/geometry/bezier';
export * from './utils/geometry/barLabelGeometry';
export * from './utils/geometry/labelPlacement';
export * from './utils/geometry/spatialIndex';
export * from './utils/geometry/fitRegion';
export * from './utils/geometry/trapezoid';
export * from './utils/geometry/scaling';
export * from './utils/geometry/lineInterpolation';
export * from './utils/zoomUtils';
export * from './logging/debugMetrics';
export { ChangeDetectableProperties } from './rendering/changeDetectableProperties';
export * from './utils/format/numberFormat';
export * from './utils/format/timeFormat';

// Rendering
export * from './rendering/domElements';
export * from './rendering/easing';
export * from './rendering/changeDetectable';
